import { useState, useEffect } from 'react';
import { useI18n } from '@/context/I18nContext';
import type { TranslationKey } from '@/i18n/types';
import { rpc } from '@/api/client';
import type { CoachGoalDto, ListSetupCoachGoalsResponse } from '@/types/telemetry';
import { ToolHeader } from '@/components/common/ToolHeader';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import {
  GraduationCap, ChevronDown, ChevronUp,
  ArrowUpRight, ArrowDownRight, Zap, Shield, Wind,
  Rocket, Sun, CloudRain, CircleDot, Gauge,
} from 'lucide-react';

// ─── Icon map ───────────────────────────────────────────
const ICON_MAP: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Shield,
  Wind,
  Rocket,
  Sun,
  CloudRain,
  CircleDot,
  Gauge,
};

// ─── Goal Card ──────────────────────────────────────────
function GoalCard({ goal, index }: { goal: CoachGoalDto; index: number }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const Icon = ICON_MAP[goal.icon] ?? GraduationCap;
  const adjustments = goal.adjustments || [];

  return (
    <div
      className="card overflow-hidden transition-all duration-200"
      style={{ animation: `data-item-in 180ms ease-out ${60 + index * 50}ms both` }}
    >
      {/* Card header — clickable */}
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-4 w-full p-4 sm:p-5 text-left cursor-pointer hover:bg-bg-hover transition-colors"
      >
        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-accent-subtle flex-shrink-0">
          <Icon size={18} className="text-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-text-primary">{t(goal.titleKey as TranslationKey)}</h3>
          <p className="text-xs text-text-secondary mt-0.5 line-clamp-1">{t(goal.descriptionKey as TranslationKey)}</p>
        </div>
        <div className="flex-shrink-0 text-text-muted">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {/* Expanded content */}
      {open && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-border-subtle">
          {/* Full description */}
          <p className="text-xs text-text-secondary leading-relaxed mt-4 mb-4">
            {t(goal.descriptionKey as TranslationKey)}
          </p>

          {/* Adjustments table — desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="divider">
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('coach.col.parameter')}</th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('coach.col.change')}</th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('coach.col.effect')}</th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">{t('coach.col.sideEffects')}</th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted w-32">{t('coach.col.effectiveness')}</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((adj, i) => (
                  <tr key={i} className={`hover:bg-bg-hover transition-colors ${i < adjustments.length - 1 ? 'divider' : ''}`}>
                    <td className="px-4 py-3 font-medium text-text-primary whitespace-nowrap">{t(adj.parameter as TranslationKey)}</td>
                    <td className="px-4 py-3 text-accent font-medium">{t(adj.change as TranslationKey)}</td>
                    <td className="px-4 py-3 text-green">{t(adj.effect as TranslationKey)}</td>
                    <td className="px-4 py-3 text-yellow">{t(adj.sideEffects as TranslationKey)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-bg-surface overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              adj.effectiveness >= 75 ? 'bg-green' :
                              adj.effectiveness >= 50 ? 'bg-yellow' : 'bg-red'
                            }`}
                            style={{ width: `${adj.effectiveness}%` }}
                          />
                        </div>
                        <span className={`text-[0.65rem] font-medium tabular-nums mono w-8 text-right ${
                          adj.effectiveness >= 75 ? 'text-green' :
                          adj.effectiveness >= 50 ? 'text-yellow' : 'text-red'
                        }`}>{adj.effectiveness}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Adjustments cards — mobile */}
          <div className="md:hidden flex flex-col gap-3">
            {adjustments.map((adj, i) => (
              <div key={i} className="rounded-md bg-bg-surface border border-border-subtle p-3">
                <div className="text-xs font-semibold text-text-primary mb-2">{t(adj.parameter as TranslationKey)}</div>
                <div className="flex flex-col gap-1.5 text-xs">
                  <div className="flex gap-2">
                    <span className="text-text-muted w-16 flex-shrink-0">{t('coach.col.change')}</span>
                    <span className="text-accent">{t(adj.change as TranslationKey)}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-text-muted w-16 flex-shrink-0">{t('coach.col.effect')}</span>
                    <span className="text-green">{t(adj.effect as TranslationKey)}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-text-muted w-16 flex-shrink-0">{t('coach.col.sideEffects')}</span>
                    <span className="text-yellow">{t(adj.sideEffects as TranslationKey)}</span>
                  </div>
                  <div className="flex gap-2 items-center">
                    <span className="text-text-muted w-16 flex-shrink-0">{t('coach.col.effectiveness')}</span>
                    <div className="flex items-center gap-2 flex-1">
                      <div className="flex-1 h-1.5 rounded-full bg-bg-hover overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            adj.effectiveness >= 75 ? 'bg-green' :
                            adj.effectiveness >= 50 ? 'bg-yellow' : 'bg-red'
                          }`}
                          style={{ width: `${adj.effectiveness}%` }}
                        />
                      </div>
                      <span className={`text-[0.65rem] font-medium tabular-nums ${
                        adj.effectiveness >= 75 ? 'text-green' :
                        adj.effectiveness >= 50 ? 'text-yellow' : 'text-red'
                      }`}>{adj.effectiveness}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────
export function SetupCoachPage() {
  const { t } = useI18n();
  const [goals, setGoals] = useState<CoachGoalDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    rpc<ListSetupCoachGoalsResponse>('ListSetupCoachGoals', {})
      .then(resp => {
        if (!cancelled) {
          setGoals(resp.goals || []);
        }
      })
      .catch(err => {
        if (!cancelled) {
          setError(err.message || 'Failed to load setup coaching goals');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <ToolHeader title={t('coach.title')} icon={GraduationCap} />

      {/* Subtitle & disclaimer */}
      <div className="mb-6">
        <p className="text-sm text-text-secondary mb-1">{t('coach.subtitle')}</p>
        <p className="text-xs text-text-muted">{t('coach.disclaimer')}</p>
      </div>

      {/* Goal cards */}
      <div className="flex flex-col gap-3">
        {goals.map((goal, i) => (
          <GoalCard key={goal.id} goal={goal} index={i} />
        ))}
      </div>
    </div>
  );
}
